import React from "react"
import type { FC } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router"

import { NzDataGrid } from "@/components/nzDataGrid"
import { PageCard } from "@/components/page-card"
import { PageTitle } from "@/components/page-title"
import { useActiveOrganizationId, useAuthentication } from "@/contexts"
import { useFormatPersonName } from "@/formatters"
import type { User } from "@/models/user"
import { userRoles } from "@/models/user-role"
import {
  createAdminGridDeleteAction,
  createAdminGridEditAction,
  createAdminGridViewAction,
  useAdminGridActionsColumn,
} from "@/modules/admin/components/admin-grid-actions-column"
import {
  canDeleteAdminUser,
  canMutateAdminUser,
  useConfirmDeleteUser,
} from "@/modules/admin/users"
import { DataGridFill, DataGridToolbar, baseColumnDef } from "@/nzds/data-grid"
import { useUsers } from "@/services/user"
import { useUrl } from "@/utils/url"

import { Link as MuiLink } from "@mui/material"
import Button from "@mui/material/Button"
import Stack from "@mui/material/Stack"
import type {
  GridColDef,
  GridRowId,
  GridSlotProps,
  GridSlots,
} from "@mui/x-data-grid-pro"

import { AddExistingUserDialog } from "./add-existing-user-dialog"

interface UserGridRecord {
  email: User["email"]
  firstName: User["firstName"]
  id: GridRowId
  lastName: User["lastName"]
  name: User["name"]
  role: User["role"]
}

const useUsersGridColumns = (): GridColDef<UserGridRecord>[] => {
  const { t } = useTranslation("common")
  const { adminUserPath } = useUrl()

  const roles = userRoles.map((role) => ({
    label: t(role.name),
    value: role.id,
  }))

  return [
    {
      ...baseColumnDef,
      field: "name",
      headerName: t("gridColumn.name.name"),
      renderCell: ({ row, value }) => (
        <MuiLink component={Link} to={adminUserPath(String(row.id))}>
          {value}
        </MuiLink>
      ),
      width: 260,
    },
    {
      ...baseColumnDef,
      field: "email",
      headerName: t("fields.email.title"),
      flex: 1,
    },
    {
      ...baseColumnDef,
      field: "role",
      headerName: t("fields.role.title"),
      minWidth: 160,
      valueFormatter: (value) => {
        const matchedRole = roles.find((role) => role.value === value)
        return matchedRole?.label ?? ""
      },
    },
  ]
}

export const AdminUsersRoute: FC = () => {
  const { t } = useTranslation("common")
  const { activeOrganizationId } = useActiveOrganizationId()
  const { activeUser, isAdminWriterUser, isOrgAdminUser } = useAuthentication()
  const canEdit = isAdminWriterUser || isOrgAdminUser
  const { formatPersonName } = useFormatPersonName()
  const { adminUserEditPath, adminUserPath, adminUsersNewPath } = useUrl()
  const [addExistingDialogOpen, setAddExistingDialogOpen] =
    React.useState(false)

  const { usersData, usersIsFetching, usersIsPlaceholderData } = useUsers({
    apiQueryOptions: {
      where: { organization_id: activeOrganizationId },
      pageSize: 200,
    },
  })

  const { confirmAndDeleteUser, deleteUserIsPending } = useConfirmDeleteUser()

  const handleDeleteClick = React.useCallback(
    (row: UserGridRecord) => {
      confirmAndDeleteUser({
        firstName: row.firstName,
        id: String(row.id),
        lastName: row.lastName,
      }).catch(() => undefined)
    },
    [confirmAndDeleteUser]
  )

  const actionsColumn = useAdminGridActionsColumn<UserGridRecord>({
    getActions: ({ row }) => {
      const userId = String(row.id)
      const actions = [
        createAdminGridViewAction(adminUserPath(userId), t("button.view")),
      ]

      if (
        canEdit &&
        canMutateAdminUser({
          isAdminWriterUser,
          isOrgAdminUser,
          targetRole: row.role,
        })
      ) {
        actions.push(
          createAdminGridEditAction(adminUserEditPath(userId), t("button.edit"))
        )
      }

      if (
        canEdit &&
        canDeleteAdminUser({
          actorId: activeUser?.id,
          isAdminWriterUser,
          isOrgAdminUser,
          targetId: row.id,
          targetRole: row.role,
        })
      ) {
        actions.push(
          createAdminGridDeleteAction({
            disabled: deleteUserIsPending,
            label: t("button.delete"),
            onDeleteClick: () => {
              handleDeleteClick(row)
            },
          })
        )
      }

      return actions
    },
    minWidth: canEdit ? 160 : 100,
    width: canEdit ? 160 : 100,
  })
  const baseColumns = useUsersGridColumns()
  const columns = [...baseColumns, actionsColumn]

  const rows: UserGridRecord[] = React.useMemo(() => {
    if (!usersData) {
      return []
    }

    return usersData.map((user) => ({
      email: user.email,
      firstName: user.firstName,
      id: user.id ?? "",
      lastName: user.lastName,
      name: formatPersonName({
        firstName: user.firstName,
        lastName: user.lastName,
      }),
      role: user.role,
    }))
  }, [formatPersonName, usersData])

  return (
    <>
      <Stack sx={{ gap: 2, height: "100%", minHeight: 0 }}>
        <PageTitle
          heading={t("admin.users")}
          primaryActions={
            canEdit ? (
              <Button
                component={Link}
                to={adminUsersNewPath}
                variant="contained"
              >
                {t("addUser.addButton")}
              </Button>
            ) : undefined
          }
          secondaryActions={
            canEdit ? (
              <Button
                onClick={() => {
                  setAddExistingDialogOpen(true)
                }}
              >
                {t("addExistingUserToOrganization.addButton")}
              </Button>
            ) : undefined
          }
        />
        <PageCard
          sx={{
            display: "flex",
            flex: 1,
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <DataGridFill>
            <NzDataGrid
              aria-label={t("admin.usersGrid.ariaLabel")}
              columns={columns}
              getRowId={(row: UserGridRecord) => row.id}
              initialState={{
                sorting: {
                  sortModel: [{ field: "name", sort: "asc" }],
                },
              }}
              loading={usersIsFetching}
              rows={usersIsPlaceholderData ? [] : rows}
              slotProps={{
                toolbar: {
                  isRecordCountLoading:
                    usersIsFetching || usersIsPlaceholderData,
                  recordCount: usersData?.length,
                } as GridSlotProps["toolbar"],
              }}
              slots={{
                toolbar: DataGridToolbar as GridSlots["toolbar"],
              }}
              sortingMode="client"
            />
          </DataGridFill>
        </PageCard>
      </Stack>
      <AddExistingUserDialog
        onClose={() => {
          setAddExistingDialogOpen(false)
        }}
        open={addExistingDialogOpen}
        organizationId={activeOrganizationId}
      />
    </>
  )
}
